import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Link
} from 'react-router-dom';
import {
  FaArrowRight, FaApple, FaCheckCircle, FaChartLine,
  FaStore, FaUsers, FaRobot, FaMosque, FaShieldAlt,
  FaUtensils, FaHeart, FaRocket
} from 'react-icons/fa';

import ContactForm from './components/Contact/ContactForm';
import TopNavBar from './components/TopNavBar/TopNavBar';
import Footer from './components/Footer/Footer';
import About from './components/About/About';
import FAQ from './components/Help/FAQ';
import Pricing from './components/Pricing/Pricing';
import TermsOfService from './components/TermsOfService/TermsOfService';
import FadeInOnScroll from './components/FadeInOnScroll/FadeInOnScroll';

import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* ============================================
   Homepage sections
============================================ */
function Hero() {
  return (
    <section className="fh-hero fh-mesh">
      <div className="fh-orb fh-orb-emerald" style={{ width: 480, height: 480, top: -120, left: -100 }} />
      <div className="fh-orb fh-orb-gold" style={{ width: 520, height: 520, bottom: -150, right: -120 }} />

      <div className="fh-container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="fh-hero-grid">
          <div>
            <div className="fh-pill" style={{ marginBottom: 26 }}>
              <span className="fh-pill-dot" />
              <span>Building the halal economy · 2026</span>
            </div>

            <h1 className="fh-hero-title">
              The operating system <br />
              for <span className="fh-gradient-text">halal commerce</span>.
            </h1>

            <p className="fh-hero-sub">
              findhalal Inc. builds the digital infrastructure powering halal restaurants, caterers,
              and wedding businesses across America. Our flagship app, <strong style={{ color: 'var(--text-main)' }}>halwaa</strong>,
              is just the beginning.
            </p>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/pricing" className="fh-btn fh-btn-primary fh-btn-lg">
                Partner with us <FaArrowRight />
              </Link>
              <a
                href="https://apps.apple.com/us/app/halwaa/id6738673791"
                target="_blank" rel="noopener noreferrer"
                className="fh-btn fh-btn-ghost fh-btn-lg"
              >
                <FaApple /> See halwaa
              </a>
            </div>

            <div style={{ display: 'flex', gap: 24, marginTop: 36, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 500 }}>
                <FaCheckCircle style={{ color: 'var(--primary-500)' }} /> 6-week pilot, no upfront cost
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 500 }}>
                <FaCheckCircle style={{ color: 'var(--primary-500)' }} /> Backed by community
              </div>
            </div>
          </div>

          <div className="fh-hero-stack">
            <div className="fh-phone fh-hero-phone-front fh-float">
              <div className="fh-phone-screen">
                <img src="/halwaa_new_UI/map_screen.png" alt="halwaa map screen" />
              </div>
            </div>
            <div className="fh-phone fh-hero-phone-back fh-float-delay">
              <div className="fh-phone-screen">
                <img src="/halwaa_new_UI/HomeScreen.png" alt="halwaa home" />
              </div>
            </div>

            <div className="fh-glass fh-hero-card">
              <div className="fh-hero-card-icon">
                <FaStore />
              </div>
              <div>
                <h5>+38% repeat visits</h5>
                <p>avg. for partner restaurants</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatStrip() {
  const stats = [
    { num: '$733B', label: 'US halal food market (2025)' },
    { num: '8.9%', label: 'CAGR through 2034' },
    { num: '8.1M', label: 'US Muslim consumers by 2050' },
    { num: '#1', label: 'Halal food discovery app' },
  ];
  return (
    <section style={{ background: 'var(--bg-cream)', padding: '0' }}>
      <div className="fh-container">
        <div className="fh-stat-strip">
          {stats.map((s, i) => (
            <FadeInOnScroll key={i}>
              <div>
                <div className="fh-stat-num">{s.num}</div>
                <div className="fh-stat-label">{s.label}</div>
              </div>
            </FadeInOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
}

function Mission() {
  return (
    <section className="section" style={{ background: 'var(--bg-cream)' }}>
      <div className="fh-container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }} className="fh-grid-stack">
          <FadeInOnScroll>
            <div className="fh-eyebrow"><FaMosque /> Our mission</div>
            <h2 style={{ marginBottom: 20 }}>
              Halal isn't a niche. <br />
              <span className="serif-italic fh-gradient-text">It's an economy.</span>
            </h2>
            <p style={{ fontSize: '1.1rem', marginBottom: 16 }}>
              From the corner kebab shop in Atlanta to the wedding caterer in New Jersey, halal businesses
              are the heartbeat of the Muslim community. But they&apos;ve been underserved by mainstream tech
              for too long.
            </p>
            <p style={{ fontSize: '1.1rem' }}>
              findhalal Inc. is building the tools, marketplaces, and AI platforms that bring halal
              commerce into 2026, and reunite the community in the process.
            </p>
          </FadeInOnScroll>

          <FadeInOnScroll>
            <div className="fh-glass" style={{ padding: 36 }}>
              <div className="fh-eyebrow" style={{ color: 'var(--gold)' }}>What we believe</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                {[
                  ['Trust over hype.', 'Every halal claim should be verifiable, not viral.'],
                  ['Owners over algorithms.', 'Technology should serve restaurant owners, not extract from them.'],
                  ['Community over commerce.', 'We grow when our partners and our users grow together.'],
                ].map(([t, d], i) => (
                  <li key={i} style={{ display: 'flex', gap: 14 }}>
                    <FaCheckCircle style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 5 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--text-main)', marginBottom: 4 }}>{t}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.96rem' }}>{d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </FadeInOnScroll>
        </div>
      </div>
      <style>{`
        @media (max-width: 980px) {
          .fh-grid-stack { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
      `}</style>
    </section>
  );
}

function ProductPortfolio() {
  return (
    <section className="section" style={{ background: 'var(--bg-main)', position: 'relative' }}>
      <div className="fh-orb fh-orb-deep" style={{ width: 360, height: 360, top: 80, right: -100 }} />
      <div className="fh-container-wide" style={{ position: 'relative', zIndex: 2 }}>
        <FadeInOnScroll>
          <div style={{ textAlign: 'center', marginBottom: 64, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
            <div className="fh-eyebrow" style={{ justifyContent: 'center' }}><FaRocket /> What we're building</div>
            <h2>One company, <span className="fh-gradient-text">three product lines.</span></h2>
            <p style={{ fontSize: '1.1rem' }}>
              Discovery for diners. Operating tools for restaurants. AI infrastructure for the halal supply chain.
            </p>
          </div>
        </FadeInOnScroll>

        <div className="fh-products">
          {/* Product 1 — halwaa app */}
          <FadeInOnScroll>
            <article className="fh-product-card fh-product-emerald">
              <div className="fh-product-text">
                <div className="fh-pill fh-pill-emerald">halwaa · consumer app</div>
                <h3>Discover halal. Verified by the community.</h3>
                <p>
                  Our flagship iOS app helps 2K+ active diners find verified halal restaurants,
                  gamify their food journey, and skip the fake reviews. Free to download.
                </p>
                <a href="https://apps.apple.com/us/app/halwaa/id6738673791" target="_blank" rel="noopener noreferrer" className="fh-btn fh-btn-dark">
                  <FaApple /> Download halwaa
                </a>
              </div>
              <div className="fh-product-visual">
                <div className="fh-phone fh-product-phone-a">
                  <div className="fh-phone-screen">
                    <img src="/halwaa_new_UI/searchAI_screen.png" alt="halwaa AI search" />
                  </div>
                </div>
                <div className="fh-phone fh-product-phone-b">
                  <div className="fh-phone-screen">
                    <img src="/halwaa_new_UI/feed.png" alt="halwaa feed" />
                  </div>
                </div>
              </div>
            </article>
          </FadeInOnScroll>

          {/* Product 2 — halwaa for business */}
          <FadeInOnScroll>
            <article className="fh-product-card fh-product-gold fh-product-reverse">
              <div className="fh-product-text">
                <div className="fh-pill fh-pill-gold">halwaa for business</div>
                <h3>The dashboard halal owners actually wanted.</h3>
                <p>
                  Online ordering, loyalty, AI menu insights, and Ramadan campaigns.
                  All in one place. No more juggling Toast, Square, Yelp, and Instagram.
                </p>
                <Link to="/pricing" className="fh-btn fh-btn-primary">
                  See partner plans <FaArrowRight />
                </Link>
              </div>
              <div className="fh-product-visual">
                <div className="fh-product-mockup">
                  <div className="fh-product-mockup-header">
                    <span className="fh-mockup-dot" />
                    <span className="fh-mockup-dot" />
                    <span className="fh-mockup-dot" />
                    <span className="fh-mockup-title">halwaa for business</span>
                  </div>
                  <div className="fh-product-mockup-body">
                    <div className="fh-mockup-stat">
                      <div className="fh-mockup-stat-num">+38%</div>
                      <div className="fh-mockup-stat-label">Repeat visits this month</div>
                    </div>
                    <div className="fh-mockup-stat">
                      <div className="fh-mockup-stat-num">142</div>
                      <div className="fh-mockup-stat-label">New catering inquiries</div>
                    </div>
                    <div className="fh-mockup-bars">
                      <span style={{ height: '40%' }} />
                      <span style={{ height: '60%' }} />
                      <span style={{ height: '85%' }} />
                      <span style={{ height: '70%' }} />
                      <span style={{ height: '95%' }} />
                      <span style={{ height: '55%' }} />
                      <span style={{ height: '75%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </FadeInOnScroll>

          {/* Product 3 — halal AI */}
          <FadeInOnScroll>
            <article className="fh-product-card fh-product-dark">
              <div className="fh-product-text">
                <div className="fh-eyebrow" style={{ color: 'var(--gold-light)' }}>
                  <FaRobot /> halal AI · coming 2026
                </div>
                <h3 style={{ color: 'white' }}>
                  AI that reads invoices, flags ingredients, and proves your halal supply chain.
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.78)' }}>
                  Restaurants spend 10+ hours a week manually verifying halal compliance.
                  Our AI scans supplier invoices, cross-references certification databases,
                  and gives you a real-time compliance score.
                </p>
              </div>
              <div className="fh-product-visual">
                <div className="fh-ai-panel">
                  {[
                    'Invoice OCR + ingredient parsing',
                    'Auto cross-check with JAKIM, IFANCA, HMS',
                    'Digital halal badge with QR for diners',
                    'Audit-ready compliance reports',
                  ].map((txt, i) => (
                    <div key={i} className="fh-ai-row">
                      <FaCheckCircle />
                      <span>{txt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </FadeInOnScroll>
        </div>
      </div>
    </section>
  );
}

function PainPoints() {
  const items = [
    {
      icon: FaUtensils,
      pain: 'Yelp & Google reviews don\'t reflect halal authenticity.',
      fix: 'Verified placement on halwaa with a community-backed halal badge.',
    },
    {
      icon: FaUsers,
      pain: 'Staff turnover is 100%+ a year. Training cost is brutal.',
      fix: 'Pre-built ordering, loyalty & reservation flows your team can run from day one.',
    },
    {
      icon: FaShieldAlt,
      pain: 'Halal certification audits take weeks of manual paperwork.',
      fix: 'AI compliance dashboard reduces audit prep by 90% (HIAS-style, but for owners).',
    },
    {
      icon: FaChartLine,
      pain: 'Catering & wedding leads come through DMs and word-of-mouth.',
      fix: 'Dedicated catering & wedding inquiry pipeline routed straight to your inbox.',
    },
    {
      icon: FaRobot,
      pain: '"What\'s selling on weekends?" lives only in the owner\'s head.',
      fix: 'AI menu analytics that flag your top earners and your dead weight.',
    },
    {
      icon: FaHeart,
      pain: 'Ramadan brings 3x demand. Most restaurants aren\'t prepared.',
      fix: 'Pre-built Ramadan campaign templates + sponsored placement to fasting diners.',
    },
  ];

  return (
    <section className="section fh-mesh-gold">
      <div className="fh-container">
        <FadeInOnScroll>
          <div style={{ textAlign: 'center', marginBottom: 56, maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}>
            <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>For restaurant owners</div>
            <h2>We've talked to <span className="fh-gradient-text-warm">halal owners.</span><br /> Here's what hurts.</h2>
            <p style={{ fontSize: '1.1rem' }}>
              Every product we ship starts with a real conversation, in a real kitchen, with a real owner.
              No fluff. No "synergy." Just fixes.
            </p>
          </div>
        </FadeInOnScroll>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="fh-pain-grid">
          {items.map((it, i) => (
            <FadeInOnScroll key={i}>
              <div className="fh-glass" style={{ padding: 28, height: '100%' }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: 'var(--primary-50)', border: '1px solid rgba(22,160,133,0.15)',
                  display: 'grid', placeItems: 'center', color: 'var(--primary)', fontSize: '1.2rem',
                  marginBottom: 18,
                }}>
                  <it.icon />
                </div>
                <div style={{ fontWeight: 700, color: 'var(--text-main)', marginBottom: 8, fontSize: '1.02rem', lineHeight: 1.4 }}>
                  "{it.pain}"
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, paddingTop: 16, borderTop: '1px dashed var(--border)' }}>
                  <FaArrowRight style={{ color: 'var(--gold)', fontSize: '0.85rem', marginTop: 6, flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-muted)' }}>{it.fix}</p>
                </div>
              </div>
            </FadeInOnScroll>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 980px) { .fh-pain-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 640px) { .fh-pain-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function PricingPreview() {
  const tiers = [
    {
      name: 'Listed',
      price: 'Free',
      tag: 'Be discovered',
      bullets: [
        'Verified profile on halwaa',
        'Community halal badge',
        'Basic photos + hours sync',
      ],
      cta: 'Claim your spot',
    },
    {
      name: 'Growth',
      price: '$99/mo',
      tag: 'Most popular',
      featured: true,
      bullets: [
        'Featured placement in your city',
        'Review insights + AI summaries',
        'Ramadan & Eid campaign tools',
        'Catering & wedding inquiries',
      ],
      cta: 'Start 6-week pilot',
    },
    {
      name: 'Pro Suite',
      price: '$299/mo',
      tag: 'Halal Business OS',
      bullets: [
        'Everything in Growth',
        'Online ordering + Shopify sync',
        'Loyalty & rewards program',
        'AI menu analytics dashboard',
        'Halal compliance reports',
      ],
      cta: 'Book a demo',
    },
  ];

  return (
    <section className="section" style={{ background: 'var(--bg-main)' }}>
      <div className="fh-container">
        <FadeInOnScroll>
          <div style={{ textAlign: 'center', marginBottom: 48, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
            <div className="fh-eyebrow" style={{ justifyContent: 'center' }}>Partner pricing</div>
            <h2>Simple plans. <span className="fh-gradient-text">No surprises.</span></h2>
            <p style={{ fontSize: '1.1rem' }}>
              Pick a tier, run a 6-week pilot, and only stay if it pays for itself.
            </p>
          </div>
        </FadeInOnScroll>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="fh-tier-grid">
          {tiers.map((t, i) => (
            <FadeInOnScroll key={i}>
              <div className="fh-glass" style={{
                padding: 32,
                height: '100%',
                position: 'relative',
                ...(t.featured ? {
                  background: 'linear-gradient(160deg, #FFFFFF 0%, var(--primary-50) 100%)',
                  border: '2px solid var(--primary)',
                  boxShadow: '0 24px 48px -12px rgba(14, 95, 74, 0.25)',
                } : {})
              }}>
                {t.featured && (
                  <span style={{
                    position: 'absolute', top: -12, right: 24,
                    background: 'var(--primary)', color: 'white',
                    padding: '6px 14px', borderRadius: 999,
                    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>{t.tag}</span>
                )}
                {!t.featured && <div className="fh-eyebrow" style={{ marginBottom: 8 }}>{t.tag}</div>}
                <h3 style={{ fontSize: '1.5rem', marginBottom: 4 }}>{t.name}</h3>
                <div style={{ fontSize: '2.4rem', fontWeight: 900, color: 'var(--text-main)', marginBottom: 4, fontFamily: 'Outfit, sans-serif' }}>
                  {t.price}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 24 }}>
                  per location
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {t.bullets.map((b, bi) => (
                    <li key={bi} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '0.95rem', color: 'var(--text-main)' }}>
                      <FaCheckCircle style={{ color: 'var(--primary-500)', flexShrink: 0, marginTop: 4 }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className={`fh-btn ${t.featured ? 'fh-btn-primary' : 'fh-btn-ghost'}`} style={{ width: '100%' }}>
                  {t.cta} <FaArrowRight />
                </Link>
              </div>
            </FadeInOnScroll>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Link to="/pricing" style={{ color: 'var(--primary)', fontWeight: 600, fontSize: '0.98rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            See full partner program <FaArrowRight style={{ fontSize: '0.85rem' }} />
          </Link>
        </div>
      </div>
      <style>{`
        @media (max-width: 980px) { .fh-tier-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}

function InvestorBand() {
  return (
    <section className="section" style={{ background: 'var(--bg-cream)', position: 'relative', overflow: 'hidden' }}>
      <div className="fh-orb fh-orb-gold" style={{ width: 500, height: 500, top: -100, right: -150 }} />
      <div className="fh-container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="fh-glass" style={{ padding: 56, textAlign: 'center', maxWidth: 880, margin: '0 auto' }}>
          <div className="fh-pill fh-pill-gold" style={{ marginBottom: 24 }}>For investors & partners</div>
          <h2 style={{ marginBottom: 18 }}>
            We're raising to scale <br />
            <span className="serif-italic fh-gradient-text">halal infrastructure.</span>
          </h2>
          <p style={{ fontSize: '1.12rem', maxWidth: 580, margin: '0 auto 28px' }}>
            $733B US halal food market. 8.9% CAGR. A community of 4M+ that's been
            served by spreadsheets and Facebook groups for 30 years. We're changing that.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:hasan@findhalal.us?subject=findhalal%20Inc%20Investor%20Inquiry" className="fh-btn fh-btn-primary fh-btn-lg">
              Request our deck <FaArrowRight />
            </a>
            <Link to="/about" className="fh-btn fh-btn-ghost fh-btn-lg">
              Meet the team
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function App() {
  return (
    <div className="app-container">
      <ScrollToTop />
      <TopNavBar />

      <Routes>
        <Route
          path="/"
          element={
            <>
              <Hero />
              <StatStrip />
              <Mission />
              <ProductPortfolio />
              <PainPoints />
              <PricingPreview />
              <InvestorBand />
              <ContactForm />
              <Footer />
            </>
          }
        />
        <Route path="/about" element={<About />} />
        <Route
          path="/contact"
          element={
            <div className="contact-page-container">
              <ContactForm />
              <Footer />
            </div>
          }
        />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/help" element={<FAQ />} />
        <Route path="/pricing" element={<Pricing />} />
      </Routes>
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;
